/**
 * ChargeSystem — generic charge accumulation for skills that build power
 * from movement, hits taken, or elapsed time.
 *
 * Used by Lightning's Static (movement-based charge) and Grit's Defiance
 * (hit-based charge). Each charge type is registered with a ChargeConfig
 * that controls its accumulation sources, maximum capacity, and decay
 * behavior. Skills consume accumulated charge to power abilities.
 *
 * @see ADR-064 (attunement shared systems architecture)
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChargeConfig {
  /** Unique identifier for this charge type (e.g., 'static_charge', 'defiance_charge'). */
  readonly id: string;
  /** Maximum charge capacity. */
  readonly maxCharge: number;
  /** Milliseconds after last accumulation before decay begins. */
  readonly decayDelayMs: number;
  /** Charge lost per second during decay. */
  readonly decayRate: number;

  // Accumulation sources — at least one should be set.
  /** Charge gained per pixel walked. */
  readonly walkChargeRate?: number;
  /** Charge gained per pixel run. */
  readonly runChargeRate?: number;
  /** Charge gained per dash event. */
  readonly dashChargeRate?: number;
  /** Charge gained per hit received. */
  readonly hitTakenChargeRate?: number;
  /** Charge gained per second (passive). */
  readonly timeChargeRate?: number;
}

export interface ChargeInstance {
  readonly config: ChargeConfig;
  current: number;
  readonly maxCharge: number;
  readonly decayDelay: number;
  readonly isFull: boolean;
  readonly isDecaying: boolean;
}

// ---------------------------------------------------------------------------
// Internal per-charge state
// ---------------------------------------------------------------------------

class ChargeState implements ChargeInstance {
  readonly config: ChargeConfig;
  current = 0;

  /** Tracks ms since last accumulation event — when this exceeds decayDelayMs, decay begins. */
  private _msSinceLastAccumulation = 0;
  /** Whether decay is currently active. */
  private _decaying = false;
  /** Optional override for max charge (set via multiplier). */
  private _maxChargeOverride: number | undefined;
  /** Optional override for decay delay (set via additive bonus). */
  private _decayDelayOverride: number | undefined;

  constructor(config: ChargeConfig) {
    this.config = config;
  }

  get maxCharge(): number {
    return this._maxChargeOverride ?? this.config.maxCharge;
  }

  set maxChargeOverride(value: number | undefined) {
    this._maxChargeOverride = value;
    if (value !== undefined && this.current > value) {
      this.current = value;
    }
  }

  get decayDelay(): number {
    return this._decayDelayOverride ?? this.config.decayDelayMs;
  }

  set decayDelayOverride(value: number | undefined) {
    this._decayDelayOverride = value;
  }

  get isFull(): boolean {
    return this.current >= this.maxCharge;
  }

  get isDecaying(): boolean {
    return this._decaying;
  }

  /** Add charge from any source, capping at maxCharge. Resets decay timer. */
  accumulate(amount: number): void {
    if (amount <= 0) return;
    this.current = Math.min(this.current + amount, this.maxCharge);
    this._msSinceLastAccumulation = 0;
    this._decaying = false;
  }

  /**
   * Consume charge — full or partial.
   * If `amount` is undefined, consumes all available charge.
   * Returns the amount actually consumed.
   */
  consume(amount?: number): number {
    if (amount === undefined) {
      const consumed = this.current;
      this.current = 0;
      return consumed;
    }
    const consumed = Math.min(amount, this.current);
    this.current -= consumed;
    return consumed;
  }

  /**
   * Per-frame update: movement accumulation, time accumulation, and decay.
   */
  update(delta: number, movementPixels: number, isRunning: boolean, isDashing: boolean): void {
    let accumulated = false;

    // Movement-based accumulation
    if (movementPixels > 0) {
      if (isRunning && this.config.runChargeRate) {
        this.accumulate(movementPixels * this.config.runChargeRate);
        accumulated = true;
      } else if (this.config.walkChargeRate) {
        this.accumulate(movementPixels * this.config.walkChargeRate);
        accumulated = true;
      }
    }

    // Dash-based accumulation (per-event, not distance-based)
    if (isDashing && this.config.dashChargeRate) {
      this.accumulate(this.config.dashChargeRate);
      accumulated = true;
    }

    // Time-based accumulation (passive)
    if (this.config.timeChargeRate) {
      const timeCharge = this.config.timeChargeRate * (delta / 1000);
      if (timeCharge > 0) {
        this.accumulate(timeCharge);
        accumulated = true;
      }
    }

    // Decay logic: count up from last accumulation, start decaying after delay
    if (!accumulated && this.current > 0) {
      this._msSinceLastAccumulation += delta;
      if (this._msSinceLastAccumulation >= this.decayDelay) {
        this._decaying = true;
        const decayAmount = this.config.decayRate * (delta / 1000);
        this.current = Math.max(0, this.current - decayAmount);
        if (this.current === 0) {
          this._decaying = false;
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// System
// ---------------------------------------------------------------------------

export class ChargeSystem {
  private readonly charges = new Map<string, ChargeState>();

  /** Register a charge type. Overwrites any existing registration with the same ID. */
  register(config: ChargeConfig): void {
    this.charges.set(config.id, new ChargeState(config));
  }

  /** Get a charge instance by ID. Returns undefined if not registered. */
  get(chargeId: string): ChargeInstance | undefined {
    return this.charges.get(chargeId);
  }

  /** Add charge from a source event (e.g., hit taken). */
  addCharge(chargeId: string, amount: number): void {
    const state = this.charges.get(chargeId);
    if (state) {
      state.accumulate(amount);
    }
  }

  /**
   * Consume charge (full or partial). Returns amount consumed.
   * If `amount` is undefined, consumes all available charge.
   */
  consume(chargeId: string, amount?: number): number {
    const state = this.charges.get(chargeId);
    if (!state) return 0;
    return state.consume(amount);
  }

  /** Override max charge for a registered charge type. Pass undefined to revert to config value. */
  setMaxCharge(chargeId: string, maxCharge: number | undefined): void {
    const state = this.charges.get(chargeId);
    if (state) {
      state.maxChargeOverride = maxCharge;
    }
  }

  /** Override decay delay for a registered charge type. Pass undefined to revert to config value. */
  setDecayDelay(chargeId: string, delayMs: number | undefined): void {
    const state = this.charges.get(chargeId);
    if (state) {
      state.decayDelayOverride = delayMs;
    }
  }

  /**
   * Per-frame update: movement accumulation, time accumulation, decay timing.
   * Call once per frame with the player's current movement state.
   */
  update(delta: number, movementPixels: number, isRunning: boolean, isDashing: boolean): void {
    for (const state of this.charges.values()) {
      state.update(delta, movementPixels, isRunning, isDashing);
    }
  }

  /** Clear all charge state. Called on scene shutdown to prevent leaks across run restarts. */
  clear(): void {
    this.charges.clear();
  }
}
